<script setup lang="ts">
/**
 * @changelog
 *
 * 2026-03-11 (0.1.0) (jhoward)
 *   - Add OfferManagementDialog.vue for redeeming or canceling offers with prize and ADA options.
 *
 * 2026-03-13 (0.2.0) (jhoward)
 *   - Add OfferManagementDialog.vue for redeeming/canceling offers with prize and ADA options.
 *
 * 2026-03-13 (0.3.0) (jhoward)
 *   - Add ADA contact phone input and pass to redemption request.
 *   - Improve ADA seating UI transitions and visibility.
 *
 * 2026-03-17 (0.4.0) (jhoward)
 *   - Add logic to detect hotel booking and redirect to booking engine if applicable.
 *   - Use getHotelOfferValidation to prefetch booking URL.
 *   - Update dialog title and confirm label dynamically for booking.
 *   - Add phone number placeholder for ADA contact.
 *
 * 2026-03-30 (0.5.0) (jhoward)
 *   - Add window.gtag event tracking for offer_book, offer_redeem, offer_void, and offer_dialog events.
 *
 * 2026-04-09 (0.5.1) (jhoward)
 *   - Update snack messages to distinguish between redeeming and canceling offers in async function redeemOffer.
 *
 * 2026-04-16 (0.5.2) (jhoward)
 *   - Update dialog title and confirm label only if booking URL is present in src\components\offers\OfferManagementDialog.vue.
 *   - Set display:none for hidden dialog elements and display:initial for ADA-required elements.
 *
 * 2026-04-29 (0.6.0) (jhoward)
 *   - Show ADA contact phone and notes fields only when ADA seating is required.
 *   - Remove unused validateBooking import.
 *   - Remove unused CSS for ADA seating fields.
 *
 * 2026-09-18 (0.7.0) (jhoward)
 *   - Give the ADA contact number pattern="phone-us" in place of the placeholder '###-###-####'. The number is
 *     appended to the seating notes the property reads, so a consistently formatted one is worth more here than the
 *     literal hash marks the guest was shown.
 */

import MaterialFormContainer from '@framework/components/material/MaterialFormContainer.vue';
import MaterialFormSwitch from '@framework/components/material/MaterialFormSwitch.vue';
import MaterialFormText from '@framework/components/material/MaterialFormText.vue';
import PeppermillDialog from '@framework/components/PeppermillDialog.vue';
import type { FetchResponse } from '@framework/defs/framework';
import type { PropType } from 'vue';
import { onMounted, ref } from 'vue';
import DisplayPrizes from '@/components/offers/DisplayPrizes.vue';
import type { OpcOffer } from '@/defs/api/OpcOffer';
import getHotelOfferValidation from '@/helpers/getHotelOfferValidation';
import peppermillCustomFetchProxy from '@/helpers/peppermillCustomFetchProxy';
import useAppStore from '@/store';
import { redeem, unredeem } from '@rss/actions/App/Http/Controllers/OfferController';

interface RedeemOfferResponse extends FetchResponse {
  data: {
    offers: OpcOffer[];
  };
}

const props = defineProps({
  offer: {
    type: Object as PropType<OpcOffer>,
    required: true,
  },
  isActive: {
    type: Boolean,
    required: false,
  },
});

const emits = defineEmits(['success', 'cancel']);

const appStore = useAppStore();
const isVoid = props.offer?.OFFER_CAN_BE_VOIDED === 'YES';

async function redeemOffer() {
  if (bookUrl.value) {
    appStore.isOverlayActive = true;
    appStore.isLoadingSpinnerActive = true;
    appStore.overlayText = 'Redirecting to booking engine...';

    window.gtag('event', 'offer_book', {
      event_category: 'offer',
      offer_code: props.offer?.CASINO_OFFER_CODE || 'unknown',
      offer_domain: props.offer?.CASINO_OFFER_DOMAIN || 'unknown',
    });

    window.location.href = bookUrl.value;
    return;
  }

  const requestData: { [key: string]: string | number } = {};

  if (!isVoid && selectedPrizeId.value) {
    requestData['prize_id'] = selectedPrizeId.value;
    requestData['prize_quantity'] = selectedPrizeQuantity.value || props.offer.PRIZE_MAX_QUANTITY;
  } else if (isVoid && props.offer.PRIZE_ID_SELECTED) {
    requestData['prize_id'] = props.offer.PRIZE_ID_SELECTED;
    requestData['prize_quantity'] = props.offer.PRIZE_QUANTITY_RESERVED || props.offer.PRIZE_MAX_QUANTITY;
  }

  if (!isVoid && isAdaSeatingRequired.value) {
    requestData['ada_seating_required'] = 'YES';
    requestData['ada_seating_notes'] = adaSeatingNotes.value;
    requestData['ada_contact_phone_number'] = adaContactPhone.value;
  }

  isLoading.value = true;

  try {
    const response = await peppermillCustomFetchProxy<RedeemOfferResponse>(requestData, {
      route: isVoid
        ? unredeem({ offerDomain: props.offer.CASINO_OFFER_DOMAIN, offerCode: props.offer.CASINO_OFFER_CODE })
        : redeem({ offerDomain: props.offer.CASINO_OFFER_DOMAIN, offerCode: props.offer.CASINO_OFFER_CODE }),
    });

    if (response.message?.length) {
      appStore.setNewSnack(15000, response.message, response.status === 200 ? 'check' : 'error');
    } else if (response.status === 200) {
      appStore.setNewSnack(15000, isVoid ? 'Offer canceled successfully' : 'Offer redeemed successfully!', 'check');
    } else {
      appStore.setNewSnack(
        15000,
        isVoid ? 'An error occurred while canceling the offer.' : 'An error occurred while redeeming the offer.',
        'error',
      );
    }

    if (200 === response.status) {
      if (response.data.offers) {
        appStore.account.offers = response.data.offers;
      }
      emits('success');

      if (isVoid) {
        window.gtag('event', 'offer_void', {
          event_category: 'offer',
          offer_code: props.offer?.CASINO_OFFER_CODE || 'unknown',
          offer_domain: props.offer?.CASINO_OFFER_DOMAIN || 'unknown',
          prize_id: selectedPrizeId.value || 'unknown',
          prize_quantity: selectedPrizeQuantity.value || 'unknown',
        });
      } else {
        window.gtag('event', 'offer_redeem', {
          event_category: 'offer',
          offer_code: props.offer?.CASINO_OFFER_CODE || 'unknown',
          offer_domain: props.offer?.CASINO_OFFER_DOMAIN || 'unknown',
          prize_id: selectedPrizeId.value || 'unknown',
          prize_quantity: selectedPrizeQuantity.value || 'unknown',
        });
      }
    }
  } catch (error) {
    console.error('Error redeeming offer:', error);
    appStore.setNewSnack(
      15000,
      'An unexpected error occurred while ' + (isVoid ? 'canceling' : 'redeeming') + ' the offer.',
      'error',
    );
  } finally {
    isLoading.value = false;
  }
}

function sendCancelEvent() {
  emits('cancel');
}

const isLoading = ref<boolean>(false);
const isAdaSeatingRequired = ref<boolean>(false);
const adaSeatingNotes = ref<string>('');
const adaContactPhone = ref<string>('');
const selectedPrizeId = ref<string | null>(null);
const selectedPrizeQuantity = ref<string | null>(null);
const bookUrl = ref<string | null>(null);
const dialogTitle = ref<string>(isVoid ? 'Cancel Offer' : 'Redeem Offer');
const dialogLabelConfirm = ref<string>(isVoid ? 'CANCEL' : 'REDEEM');

onMounted(async () => {
  if (!isVoid) {
    bookUrl.value = await getHotelOfferValidation(props.offer);
    if (bookUrl.value) {
      dialogTitle.value = 'Book Room';
      dialogLabelConfirm.value = 'BOOK';
    }
  }

  window.gtag('event', 'offer_dialog', {
    event_category: 'offer',
    offer_code: props.offer?.CASINO_OFFER_CODE || 'unknown',
    offer_domain: props.offer?.CASINO_OFFER_DOMAIN || 'unknown',
  });
});
</script>

<template>
  <div class="offer_management--dialog">
    <peppermill-dialog
      :title="dialogTitle"
      :is-active="isActive && !isLoading"
      :label-confirm="dialogLabelConfirm"
      label-deny="CLOSE"
      :callback-confirm="redeemOffer"
      :callback-deny="sendCancelEvent"
    >
      <material-form-container> </material-form-container>
      <div>
        <display-prizes
          v-if="offer._PRIZES.length"
          :offer="offer"
          :prizes-selectable="'YES' === offer.OFFER_CAN_BE_REDEEMED"
          @update:selected-prize-quantity="selectedPrizeQuantity = $event"
          @update:selected-prize-id="selectedPrizeId = $event"
        ></display-prizes>
        <template v-if="['CONCERTS'].includes(offer.OFFER_COPY_CATEGORY) && !isVoid">
          <material-form-switch
            label="ADA Seating Required?"
            name="ada_seating_required"
            value="YES"
            @update:value="isAdaSeatingRequired = $event"
          ></material-form-switch>
          <material-form-text
            v-if="isAdaSeatingRequired"
            key="ada_phone"
            pattern="phone-us"
            label="Contact Phone Number"
            name="ada_contact_phone_number"
            @update:value="adaContactPhone = $event"
          ></material-form-text>
          <material-form-text
            v-if="isAdaSeatingRequired"
            key="ada_notes"
            label="ADA Seating Notes"
            help-text="Max 100 Characters"
            name="ada_seating_notes"
            @update:value="adaSeatingNotes = $event"
            type="textarea"
          ></material-form-text>
        </template>
      </div>
    </peppermill-dialog>
  </div>
</template>

<style scoped></style>
